(() => {
  const u_getCart = () =>
    window.Cart || customElements.get("cart-element") || null;

  const u_money = (cents, currency, locale) => {
    if (cents == null || Number.isNaN(Number(cents))) return "—";
    return new Intl.NumberFormat(locale || undefined, {
      style: "currency",
      currency: currency || "USD",
      currencyDisplay: "narrowSymbol",
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    }).format(Number(cents) / 100);
  };

  const u_packCountFromValue = (value) => {
    if (value == null) return 1;
    const m = String(value).match(/\d+/);
    return m ? parseInt(m[0], 10) : 1;
  };

  const u_sellingPlanIdStr = (id) => (id == null ? null : String(id));

  /*
   * Default subscription (“Deliver every”) by pack — edit here when plans change.
   * Pack number = digits in your first variant option label (example: "18-Pack" → 18).
   * Under each pack, add short text snippets that appear in the plan name in Shopify
   * (case does not matter). First matching plan in the store’s list wins; if none match,
   * the first plan in the dropdown is used.
   * To find exact names: Admin → product → subscription / selling plans, or view the
   * product page HTML and search for data-ds-pdp-config (plan_name inside JSON).
   * Optional: set window.DS_PDP_SUBSCRIPTION_DEFAULTS before this file to override
   * only the packs you pass (merged with the object below).
   */
  const DS_PDP_SUBSCRIPTION_DEFAULTS = {
    18: ["every 2 months", "every 2 month", "bi-month", "bi monthly", "bimonthly"],
    12: ["every 2 months", "every 2 month", "bi-month", "bi monthly", "bimonthly"],
    6: ["every 1 month", "delivery every 1 month", "monthly"],
  };

  const u_subscriptionDefaultsMerged = () => {
    const w =
      typeof window !== "undefined" ? window.DS_PDP_SUBSCRIPTION_DEFAULTS : null;
    if (!w || typeof w !== "object") return DS_PDP_SUBSCRIPTION_DEFAULTS;
    return { ...DS_PDP_SUBSCRIPTION_DEFAULTS, ...w };
  };

  const u_planNameMatchesAnyPhrase = (planName, phrases) => {
    const hay = String(planName || "").toLowerCase();
    return phrases.some((p) => hay.includes(String(p).toLowerCase()));
  };

  const u_defaultSellingPlanIdForPack = (allocations, packCount) => {
    if (!allocations?.length) return null;
    const fallback = u_sellingPlanIdStr(allocations[0].selling_plan_id);
    const phrases = u_subscriptionDefaultsMerged()[packCount];
    if (!phrases?.length) return fallback;
    const hit = allocations.find((a) =>
      u_planNameMatchesAnyPhrase(a.plan_name, phrases),
    );
    return hit ? u_sellingPlanIdStr(hit.selling_plan_id) : fallback;
  };

  const u_OnElementLoad = (selector, callback) => {
    let elapsed = 0;
    const interval = 100;
    const timeout = 10000;
    const timer = setInterval(() => {
      const element = document.querySelector(selector);
      elapsed += interval;
      if (element || elapsed >= timeout) {
        clearInterval(timer);
        callback(element);
      }
    }, interval);
  };

  if (typeof window.dsShopableVideoMuted !== "boolean") {
    window.dsShopableVideoMuted = true;
  }

  document.addEventListener("alpine:init", () => {
    Alpine.data("DSProduct", () => ({
      product: null,
      currentVariant: null,
      init() {
        this.product = JSON.parse(this.$el.dataset.product);
        this.currentVariant = JSON.parse(this.$el.dataset.currentVariant);
      },
    }));

    Alpine.data("DSPdpWidget", () => ({
      config: null,
      /** @type {Record<string, string | null>} */
      selected: { 1: null, 2: null, 3: null },
      purchaseType: "subscription",
      sellingPlanId: null,
      currentVariant: null,
      quantity: 1,
      /** @type {string} Theme `theme:product:{id}:variantChanged` id (section_product) */
      productId: "",

      init() {
        const script = this.$el.querySelector("[data-ds-pdp-config]");
        if (!script || !script.textContent.trim()) return;
        this.config = JSON.parse(script.textContent);
        this.productId = this.$el.dataset.dsPdpProductId || "";
        const initSel = this.config.initialSelected || {};
        this.selected = {
          1: initSel["1"] ?? null,
          2: initSel["2"] ?? null,
          3: initSel["3"] ?? null,
        };
        this.syncCurrentVariant();
        if (!this.hasSubscription) this.purchaseType = "one_time";
        this.syncSellingPlan();
        this.$watch(
          "selected",
          () => {
            this.syncCurrentVariant();
            this.syncSellingPlan();
            this.emitThemeVariant();
            this.syncThemeVariantInputs();
          },
          { deep: true },
        );
        this.$watch("purchaseType", () => {
          this.syncSellingPlan();
          window.dispatchEvent(new CustomEvent("ds:pdp-purchase-type"));
        });
        this.$watch(() => this.quantity, () => this.syncQuantityToForm());
        this.$nextTick(() => {
          this.dsPdpAfterFirstPaint();
          window.dispatchEvent(new CustomEvent("ds:pdp-purchase-type"));
        });

        // remove appstle widget
        u_OnElementLoad(".appstle_sub_widget", (element) => {
          element?.remove();
        });
      },

      /** SV- form + subscribe UI after Alpine paints x-if dropdown */
      dsPdpAfterFirstPaint() {
        this.syncSellingPlan();
        this.syncQuantityToForm();
        this.emitThemeVariant();
        this.syncThemeVariantInputs();
        this.$nextTick(() => this.syncSellingPlan());
        requestAnimationFrame(() => this.syncSellingPlan());
      },

      themeVariantPayload() {
        const v = this.currentVariant;
        if (!v) return null;
        return {
          id: v.id,
          available: v.available,
          public_title: v.title || "",
          sku: v.sku || "",
          inventory_management: v.inventory_management || "",
          inventory_policy: v.inventory_policy || "",
          inventory_quantity: v.inventory_quantity ?? 0,
        };
      },

      emitThemeVariant() {
        if (!this.productId) return;
        const v = this.themeVariantPayload();
        if (!v) return;
        window.trigger(`theme:product:${this.productId}:variantChanged`, {
          variant: v,
        });
      },

      /**
       * Keeps theme `product-buy-buttons--input` (and fallback hidden `id`) in sync
       * with DS pack / option selection. Previously we skipped DOM updates whenever
       * buy-buttons existed, so variant_id never changed after pick.
       */
      syncThemeVariantInputs() {
        const form = this.$el.closest("form");
        if (!form) return;
        const v = this.currentVariant;
        const themeInputs = form.querySelectorAll(
          "input.product-buy-buttons--input[name='id']",
        );
        if (v && themeInputs.length) {
          themeInputs.forEach((inp) => {
            inp.value = String(v.id);
            inp.setAttribute("data-id", String(v.id));
            inp.setAttribute("data-sku", v.sku || "");
            inp.setAttribute("data-management", v.inventory_management || "");
            inp.setAttribute("data-policy", v.inventory_policy || "");
            inp.setAttribute("data-quantity", String(v.inventory_quantity ?? 0));
          });
          return;
        }
        if (!v) return;
        let inp = form.querySelector("input[data-ds-pdp-fallback-id]");
        if (!inp) {
          inp = document.createElement("input");
          inp.type = "hidden";
          inp.name = "id";
          inp.setAttribute("data-ds-pdp-fallback-id", "");
          form.appendChild(inp);
        }
        inp.value = String(v.id);
      },

      syncQuantityToForm() {
        const form = this.$el.closest("form");
        if (!form) return;
        const q = String(this.qtyLineMultiplier());
        form.querySelectorAll("[name=quantity]").forEach((el) => {
          if (el instanceof HTMLInputElement || el instanceof HTMLSelectElement) {
            el.value = q;
          }
        });
      },

      async handlePdpAddToCart() {
        const form = this.$el.closest("form");
        if (!form || !this.currentVariant?.available) return;
        this.syncQuantityToForm();
        this.emitThemeVariant();
        this.syncThemeVariantInputs();

        const buyRoot = form.querySelector("product-buy-buttons-element");
        if (buyRoot) {
          buyRoot.querySelectorAll(".product-buy-buttons--primary").forEach((btn) => {
            btn.setAttribute("aria-busy", "true");
          });
          if (typeof form.requestSubmit === "function") {
            form.requestSubmit();
          } else {
            form.dispatchEvent(
              new Event("submit", { bubbles: true, cancelable: true }),
            );
          }
          return;
        }

        await this.addToCartWithoutBuyButtons(form);
      },

      async addToCartWithoutBuyButtons(form) {
        const Cart = u_getCart();
        const submitBtn = this.$refs.pdpSubmit;
        submitBtn?.setAttribute?.("aria-busy", "true");
        if (typeof Cart === "undefined" || typeof Cart.addItems !== "function") {
          submitBtn?.removeAttribute?.("aria-busy");
          form.submit();
          return;
        }
        const fd = new FormData(form);
        const sections = Cart.sections ? Cart.sections.join(",") : "cart-drawer";
        const body = `${new URLSearchParams(fd).toString()}&sections=${sections}`;
        try {
          await Cart.addItems(
            {
              method: "POST",
              headers: { "Content-Type": "application/x-www-form-urlencoded" },
              body,
            },
            submitBtn || null,
          );
        } catch {
          /* theme feedback / errors */
        } finally {
          submitBtn?.removeAttribute?.("aria-busy");
        }
      },

      get isSubscription() {
        return this.purchaseType === "subscription" && this.hasSubscription;
      },

      get variants() {
        return this.config?.variants || [];
      },

      get options() {
        return this.config?.options || [];
      },

      syncCurrentVariant() {
        // SV- keep variant in sync for Alpine deps
        if (!this.config?.options?.length) {
          this.currentVariant = this.variants[0] || null;
          return;
        }
        const v =
          this.variants.find((variant) =>
            this.options.every(
              (opt) =>
                variant[`option${opt.position}`] ===
                this.selected[String(opt.position)],
            ),
          ) || null;
        this.currentVariant = v;
      },

      get hasSubscription() {
        const v = this.currentVariant;
        return Boolean(
          v?.selling_plan_allocations && v.selling_plan_allocations.length > 0,
        );
      },

      get currentAllocations() {
        return this.currentVariant?.selling_plan_allocations || [];
      },

      get activeAllocation() {
        if (this.purchaseType !== "subscription" || !this.sellingPlanId) {
          return null;
        }
        return (
          this.currentAllocations.find(
            (a) =>
              u_sellingPlanIdStr(a.selling_plan_id) ===
              u_sellingPlanIdStr(this.sellingPlanId),
          ) || this.currentAllocations[0] || null
        );
      },

      get caseChoices() {
        const first = this.options[0];
        if (!first) return [];
        const overlays = this.config?.caseChoiceImages || {};
        return first.values.map((value) => {
          const pick = this.pickVariantForOption(1, value);
          const override = overlays[value];
          const fromVariant = pick?.featured_image || null;
          return {
            value,
            image: override || fromVariant,
            available: pick ? pick.available : false,
          };
        });
      },

      pickVariantForOption(position, value) {
        const pos = Number(position);
        const candidates = this.variants.filter(
          (v) => v[`option${pos}`] === value,
        );
        if (!candidates.length) return null;
        const wanted = { ...this.selected, [String(pos)]: value };
        const exact = candidates.find((v) =>
          this.options.every(
            (opt) => v[`option${opt.position}`] === wanted[String(opt.position)],
          ),
        );
        if (exact) return exact;
        return candidates.find((v) => v.available) || candidates[0];
      },

      setOption(position, value) {
        const pick = this.pickVariantForOption(position, value);
        if (!pick) return;
        this.options.forEach((opt) => {
          this.selected[String(opt.position)] = pick[`option${opt.position}`];
        });
      },

      onOptionChange() {
        /* native select updates x-model */
      },

      syncSellingPlanDeliverySelectDom() {
        // SV- align native select after x-if/x-for paint
        if (
          this.purchaseType !== "subscription" ||
          !this.hasSubscription ||
          this.sellingPlanId == null
        ) {
          return;
        }
        const sel = this.$el?.querySelector?.(
          "select.ds-pdp-widget__select--delivery[name='selling_plan']",
        );
        if (!(sel instanceof HTMLSelectElement) || !sel.options?.length) return;
        const v = String(this.sellingPlanId);
        if (sel.value !== v) sel.value = v;
      },

      syncSellingPlan() {
        if (this.purchaseType === "subscription" && !this.hasSubscription) {
          this.purchaseType = "one_time";
        }
        if (this.purchaseType === "subscription" && this.hasSubscription) {
          const ids = new Set(
            this.currentAllocations.map((a) =>
              u_sellingPlanIdStr(a.selling_plan_id),
            ),
          );
          const cur = u_sellingPlanIdStr(this.sellingPlanId);
          if (cur === null || !ids.has(cur)) {
            this.sellingPlanId = u_defaultSellingPlanIdForPack(
              this.currentAllocations,
              this.packCount(),
            );
          }
        } else {
          this.sellingPlanId = null;
        }
        this.syncSellingPlanDeliverySelectDom();
      },

      formatMoney(cents) {
        return u_money(cents, this.config?.currency, this.config?.locale);
      },

      formatPlanLabel(allocation) {
        const n = allocation.plan_name;
        if (n && String(n).toLowerCase().includes("every")) return String(n).toUpperCase();
        return n ? String(n).toUpperCase() : "SUBSCRIPTION";
      },

      packCount() {
        const firstPos = this.options[0]?.position ?? 1;
        return u_packCountFromValue(this.selected[String(firstPos)]);
      },

      get oneTimePrice() {
        return this.currentVariant?.price ?? 0;
      },

      /** Unit quantity for line totals (integer ≥ 1). */
      qtyLineMultiplier() {
        const q = parseInt(this.quantity, 10);
        return Number.isFinite(q) && q >= 1 ? q : 1;
      },

      get oneTimePriceTotal() {
        return this.oneTimePrice * this.qtyLineMultiplier();
      },

      get subPriceTotal() {
        return this.subPrice * this.qtyLineMultiplier();
      },

      get oneTimePerBottle() {
        const n = this.packCount();
        const p = this.oneTimePrice;
        return n > 0 ? Math.round(p / n) : p;
      },

      get subPrice() {
        if (!this.hasSubscription) return this.oneTimePrice;
        const a = this.activeAllocation || this.currentAllocations[0];
        return a?.price ?? this.oneTimePrice;
      },

      get subCompareLine() {
        const cmp = this.oneTimePrice;
        const price = this.subPrice;
        return {
          show: Boolean(this.hasSubscription && cmp && cmp > price),
          compare: cmp,
        };
      },

      get subPerBottle() {
        const n = this.packCount();
        const p = this.subPrice;
        return n > 0 ? Math.round(p / n) : p;
      },

      get savePercent() {
        if (!this.hasSubscription || !this.oneTimePrice) return null;
        return 15;
      },

      get priceLine() {
        const v = this.currentVariant;
        if (!v) return { price: 0, compare_at: null, save: 0 };
        const useSub = this.purchaseType === "subscription" && this.hasSubscription;
        const a = this.activeAllocation || this.currentAllocations[0];
        const price = useSub ? (a?.price ?? v.price) : v.price;
        const compare = useSub
          ? a?.compare_at_price || v.compare_at_price
          : v.compare_at_price;
        const save =
          compare && compare > price ? compare - price : 0;
        return { price, compare_at: compare, save };
      },
    }));

    const DS_GIFT_DISMISS_KEY = "ds-cart-gift-dismissed";

    const ds_giftWasDismissed = () => {
      try {
        return sessionStorage.getItem(DS_GIFT_DISMISS_KEY) === "1";
      } catch {
        return false;
      }
    };

    const ds_setGiftDismissed = (value) => {
      try {
        sessionStorage.setItem(DS_GIFT_DISMISS_KEY, value ? "1" : "0");
      } catch {
        /* sessionStorage unavailable */
      }
      window.dispatchEvent(new CustomEvent("ds:cart-gift-dismissed"));
    };

    const ds_fetchCart = async () => {
      const route = window.Shopify?.routes?.cart || "/cart";
      const res = await fetch(`${route}.js`);
      if (!res.ok) throw new Error(res.statusText);
      return res.json();
    };

    const ds_variantInCart = (items, variantId) => {
      if (!variantId || !items?.length) return false;
      return items.some((item) => item.variant_id === variantId);
    };

    Alpine.data("DSResurrectionUpsell", () => ({
      config: null,
      hidden: false,
      timerActive: true,
      timerLabel: "",
      timerId: null,
      /** @type {number | null} */
      timerEnd: null,
      /** @type {(() => void) | null} */
      _cartVisibilityHandler: null,

      init() {
        const script = this.$el.querySelector("[data-ds-upsell-config]");
        if (!script?.textContent?.trim()) return;
        this.config = JSON.parse(script.textContent);
        this.initTimer();
        if (this.config?.context === "cart") {
          this.hidden = true;
          void this.syncCartUpsellVisibility();
          this._cartVisibilityHandler = () => {
            void this.syncCartUpsellVisibility();
          };
          window.addEventListener("theme:cart:updated", this._cartVisibilityHandler);
        }
      },

      destroy() {
        if (this.timerId != null) clearInterval(this.timerId);
        if (this._cartVisibilityHandler) {
          window.removeEventListener("theme:cart:updated", this._cartVisibilityHandler);
        }
      },

      async syncCartUpsellVisibility() {
        if (this.config?.context !== "cart") return;
        const variantId = this.config?.variantId;
        if (!variantId) {
          this.hidden = true;
          return;
        }
        try {
          const cart = await ds_fetchCart();
          const inCart = ds_variantInCart(cart.items, variantId);
          const hasSubscription = cart.items.some(
            (item) => item.selling_plan_allocation,
          );
          this.hidden = inCart || hasSubscription;
        } catch {
          this.hidden = true;
        }
      },

      get price() {
        return this.config?.price ?? 0;
      },

      get compareAtPrice() {
        const c = this.config?.compareAtPrice;
        return c && c > this.price ? c : null;
      },

      get saveAmount() {
        const compare = this.compareAtPrice;
        return compare ? compare - this.price : 0;
      },

      get badgeLabel() {
        return this.saveAmount > 0 ? `Save ${this.formatMoney(this.saveAmount)}` : "";
      },

      get saveLabel() {
        return this.saveAmount > 0 ? `you save ${this.formatMoney(this.saveAmount)}` : "";
      },

      formatMoney(cents) {
        return u_money(cents, this.config?.currency, this.config?.locale);
      },

      formatTimer(totalSec) {
        const hh = Math.floor(totalSec / 3600);
        const mm = Math.floor((totalSec % 3600) / 60);
        const ss = totalSec % 60;
        if (hh > 0) {
          return `${String(hh).padStart(2, "0")}:${String(mm).padStart(2, "0")}:${String(ss).padStart(2, "0")}`;
        }
        return `${String(mm).padStart(2, "0")}:${String(ss).padStart(2, "0")}`;
      },

      initTimer() {
        const minutes = Number(this.config?.timerMinutes) || 60;
        const durationMs = minutes * 60 * 1000;
        const key = "ds-upsell-timer-end";
        let end = null;
        try {
          const stored = sessionStorage.getItem(key);
          if (stored) end = parseInt(stored, 10);
        } catch {
          /* sessionStorage unavailable */
        }
        const now = Date.now();
        if (!end || Number.isNaN(end) || end <= now) {
          end = now + durationMs;
          try {
            sessionStorage.setItem(key, String(end));
          } catch {
            /* sessionStorage unavailable */
          }
        }
        this.timerEnd = end;
        this.tickTimer();
        this.timerId = setInterval(() => this.tickTimer(), 1000);
      },

      tickTimer() {
        if (this.timerEnd == null) return;
        const remaining = this.timerEnd - Date.now();
        if (remaining <= 0) {
          this.timerActive = false;
          if (this.timerId != null) {
            clearInterval(this.timerId);
            this.timerId = null;
          }
          return;
        }
        const totalSec = Math.ceil(remaining / 1000);
        const timer = this.formatTimer(totalSec);
        const template = this.config?.timerText || "Discounted pricing for the next [timer]";
        this.timerLabel = template.replace("[timer]", timer);
      },

      async addUpsell() {
        const Cart = u_getCart();
        const btn = this.$refs.upsellCta;
        if (!this.config?.variantId) return;
        btn?.setAttribute?.("aria-busy", "true");
        if (typeof Cart === "undefined" || typeof Cart.addItems !== "function") {
          btn?.removeAttribute?.("aria-busy");
          return;
        }
        const sections = Cart.sections ? Cart.sections.join(",") : "cart-drawer";
        const body = new URLSearchParams({
          id: String(this.config.variantId),
          quantity: "1",
          "properties[ds_resurrection_upsell]": "true",
          sections,
        }).toString();
        try {
          await Cart.addItems(
            {
              method: "POST",
              headers: { "Content-Type": "application/x-www-form-urlencoded" },
              body,
            },
            btn || null,
          );
          this.hidden = true;
        } catch {
          /* theme feedback / errors */
        } finally {
          btn?.removeAttribute?.("aria-busy");
        }
      },
    }));

    const DS_GIFT_STATE = { removing: false, adding: false, syncing: false };
    /** @type {(() => void) | null} */
    let dsCartRewardsActiveSync = null;

    const DS_FREE_GIFT_PROPERTY_KEYS = ["ds_free_gift", "_ds_free_gift"];

    const ds_isAutoGiftLine = (item, variantId) => {
      if (!item || item.variant_id !== variantId) return false;
      return DS_FREE_GIFT_PROPERTY_KEYS.some((key) => {
        const value = item.properties?.[key];
        return value != null && value !== false && value !== "";
      });
    };

    const ds_findAllAutoGiftLines = (items, variantId) => {
      if (!variantId || !items?.length) return [];
      return items.filter((item) => ds_isAutoGiftLine(item, variantId));
    };

    const ds_findAutoGiftLine = (items, variantId) =>
      ds_findAllAutoGiftLines(items, variantId)[0] || null;

    const ds_findRemovableGiftLines = (items, variantId) =>
      ds_findAllAutoGiftLines(items, variantId);

    const ds_rewardsSubtotal = (cart, variantId) => {
      let subtotal = cart.items_subtotal_price;
      const giftLine = ds_findAutoGiftLine(cart.items, variantId);
      if (giftLine) subtotal -= giftLine.final_line_price;
      return subtotal;
    };

    const ds_applyCartSections = (sections) => {
      const Cart = u_getCart();
      if (Cart?.updateAllHtml && sections) {
        Cart.updateAllHtml(sections);
      }
    };

    const ds_swapCartDrawerRewards = (section) => {
      const cart = document.querySelector('cart-element[data-view="drawer"]');
      if (!cart) return;

      const form = ds_parseCartForm(section);
      const next = form?.querySelector(".ds-cart-rewards");

      cart.querySelectorAll(".cart--shipping").forEach((el) => {
        if (!el.classList.contains("ds-cart-rewards")) el.remove();
      });

      const current = cart.querySelector(".ds-cart-rewards");
      const anchor = cart.querySelector(".cart--body") || cart.querySelector(".cart--header");
      if (next && current) {
        current.replaceWith(next);
      } else if (next && !current && anchor) {
        if (anchor.classList.contains("cart--body")) anchor.before(next);
        else anchor.after(next);
      }
    };

    const ds_swapCartDrawerUpsell = (section) => {
      if (!section?.parse) return;
      const form = section.parse(".cart--form");
      if (!form) return;
      const cart = document.querySelector('cart-element[data-view="drawer"]');
      if (!cart) return;
      const next = form.querySelector(".ds-upsell--cart");
      cart.querySelectorAll(".ds-upsell--cart").forEach((el) => el.remove());
      if (next) {
        cart.querySelector(".cart--body")?.after(next);
      }
    };

    const ds_dedupeCartUpsells = () => {
      const cart = document.querySelector('cart-element[data-view="drawer"]');
      if (!cart) return;
      const upsells = cart.querySelectorAll(".ds-upsell--cart");
      if (upsells.length <= 1) return;
      const body = cart.querySelector(".cart--body");
      const keep =
        body?.nextElementSibling?.classList?.contains("ds-upsell--cart")
          ? body.nextElementSibling
          : upsells[0];
      upsells.forEach((el) => {
        if (el !== keep) el.remove();
      });
    };

    if (window._dsCartDrawerUpsellListener) {
      window.removeEventListener(
        "theme:cart:drawer:updateHtml",
        window._dsCartDrawerUpsellListener,
      );
    }
    window._dsCartDrawerUpsellListener = ({ detail }) => {
      requestAnimationFrame(() => {
        ds_swapCartDrawerRewards(detail?.section);
        ds_swapCartDrawerUpsell(detail?.section);
        ds_dedupeCartUpsells();
        const cart = document.querySelector('cart-element[data-view="drawer"]');
        const upsell = cart?.querySelector(".ds-upsell--cart");
        if (upsell && window.Alpine?.initTree) {
          window.Alpine.initTree(upsell);
        }
        window.dispatchEvent(new CustomEvent("ds:cart-gift-dismissed"));
      });
    };
    window.addEventListener(
      "theme:cart:drawer:updateHtml",
      window._dsCartDrawerUpsellListener,
    );

    const ds_getRewardsRoot = () =>
      document.querySelector('cart-element[data-view="drawer"] .ds-cart-rewards');

    const ds_getRewardsConfig = () => {
      const script = ds_getRewardsRoot()?.querySelector("[data-ds-cart-rewards-config]");
      if (!script?.textContent?.trim()) return null;
      try {
        return JSON.parse(script.textContent);
      } catch {
        return null;
      }
    };

    const ds_cartHasSubscription = (items, giftVariantId) => {
      if (!items?.length) return false;
      return items.some(
        (item) =>
          item.selling_plan_allocation &&
          (!giftVariantId || item.variant_id !== giftVariantId),
      );
    };

    const ds_paintCartRewards = (cart) => {
      const root = ds_getRewardsRoot();
      const config = ds_getRewardsConfig();
      if (!root || !config || !cart) return;

      const variantId = config.giftVariantId;
      const tier1 = Number(config.tier1ThresholdCents);
      const tier2 = Number(config.tier2ThresholdCents);
      const tier1Center = Number(config.tier1Center) || 40;
      const tier2End = Number(config.tier2End) || 90;
      if (!tier1 || !tier2) return;

      const giftLine = ds_findAutoGiftLine(cart.items, variantId);
      const subtotal = ds_rewardsSubtotal(cart, variantId);
      const hasAutoGift = Boolean(giftLine);
      const hasSubscription = ds_cartHasSubscription(cart.items, variantId);

      let progress = 0;
      let shippingReached = false;
      let giftReached = false;
      let message = config.messageAfter || "";

      root.classList.toggle("ds-cart-rewards--sub-gift", hasSubscription);

      if (hasSubscription) {
        giftReached = true;
        if (subtotal >= tier1) {
          shippingReached = true;
          progress = 100;
          message = config.messageAfter || "";
        } else {
          const remaining = u_money(tier1 - subtotal, config.currency, config.locale);
          message = String(config.messageBefore || "").replace("[$]", remaining);
          progress = tier1 ? Math.min(tier2End, (subtotal * tier2End) / tier1) : 0;
        }
      } else if (hasAutoGift || subtotal >= tier2) {
        shippingReached = true;
        giftReached = true;
        progress = 100;
        message = config.messageAfter || "";
      } else if (subtotal >= tier1) {
        shippingReached = true;
        const remaining = u_money(
          tier2 - subtotal,
          config.currency,
          config.locale,
        );
        message = String(config.messageNext || config.messageBefore || "").replace(
          "[$]",
          remaining,
        );
        const range = tier2 - tier1;
        const span = tier2End - tier1Center;
        progress = range
          ? Math.min(tier2End, tier1Center + ((subtotal - tier1) * span) / range)
          : tier2End;
      } else {
        const remaining = u_money(
          tier1 - subtotal,
          config.currency,
          config.locale,
        );
        message = String(config.messageBefore || "").replace("[$]", remaining);
        progress = tier1 ? (subtotal * tier1Center) / tier1 : 0;
      }

      const fill = root.querySelector(".ds-cart-rewards__fill");
      const msg = root.querySelector(".ds-cart-rewards__message");
      const shippingTier = root.querySelectorAll(".ds-cart-rewards__tier--shipping");
      const giftTier = root.querySelectorAll(".ds-cart-rewards__tier--gift");
      if (fill) fill.style.width = `${progress}%`;
      if (msg) msg.textContent = message;
      shippingTier.forEach((el) => el.classList.toggle("is-reached", shippingReached));
      giftTier.forEach((el) => el.classList.toggle("is-reached", giftReached));
    };

    const ds_parseCartForm = (section) => {
      if (!section) return null;
      if (typeof section.parse === "function") return section.parse(".cart--form");
      if (typeof section === "string") {
        return new DOMParser()
          .parseFromString(section, "text/html")
          .querySelector(".cart--form");
      }
      return null;
    };

    const ds_syncCartGift = async () => {
      let cart;
      try {
        cart = await ds_fetchCart();
      } catch {
        return;
      }
      ds_paintCartRewards(cart);

      const config = ds_getRewardsConfig();
      const variantId = config?.giftVariantId;
      const threshold = Number(config?.tier2ThresholdCents);
      const property = config?.giftProperty || "ds_free_gift";
      if (!variantId || !threshold) return;
      if (DS_GIFT_STATE.syncing || DS_GIFT_STATE.removing || DS_GIFT_STATE.adding) return;

      DS_GIFT_STATE.syncing = true;
      try {
        const giftLines = ds_findRemovableGiftLines(cart.items, variantId);
        if (giftLines.length > 1) {
          await ds_removeGiftLine(giftLines[1]);
          return;
        }

        const subtotal = ds_rewardsSubtotal(cart, variantId);
        const giftLine = giftLines[0] || null;
        const hasSubscription = ds_cartHasSubscription(cart.items, variantId);
        const qualifies = !hasSubscription && subtotal >= threshold;
        const variantInCart = ds_variantInCart(cart.items, variantId);

        if (!qualifies) {
          if (giftLine) await ds_removeGiftLine(giftLine);
          return;
        }

        if (giftLine) return;
        if (variantInCart) return;

        await ds_addGift(variantId, property);
      } finally {
        DS_GIFT_STATE.syncing = false;
      }
    };

    const ds_addGift = async (variantId, property) => {
      if (DS_GIFT_STATE.adding) return;
      DS_GIFT_STATE.adding = true;

      const Cart = u_getCart();
      const sections = Cart?.sections ? Cart.sections.join(",") : "cart-drawer";
      const body = new URLSearchParams({
        id: String(variantId),
        quantity: "1",
        [`properties[${property}]`]: "true",
        sections,
      });

      try {
        const cart = await ds_fetchCart();
        if (
          ds_findAutoGiftLine(cart.items, variantId) ||
          ds_variantInCart(cart.items, variantId)
        ) {
          return;
        }

        if (Cart?.addItems) {
          await Cart.addItems(
            {
              method: "POST",
              headers: { "Content-Type": "application/x-www-form-urlencoded" },
              body: body.toString(),
            },
            null,
          );
        } else {
          const res = await fetch(`${window.Shopify?.routes?.cart_add || "/cart/add"}.js`, {
            method: "POST",
            headers: { "Content-Type": "application/x-www-form-urlencoded" },
            body: body.toString(),
          });
          if (!res.ok) throw new Error(res.statusText);
          const data = await res.json();
          ds_applyCartSections(data.sections);
        }
      } catch {
        /* cart sync */
      } finally {
        DS_GIFT_STATE.adding = false;
      }
    };

    const ds_removeGiftLine = async (line) => {
      if (DS_GIFT_STATE.removing) return;
      DS_GIFT_STATE.removing = true;
      const Cart = u_getCart();
      const sections = Cart?.sections || ["cart-drawer"];

      try {
        const res = await fetch(
          `${window.Shopify?.routes?.cart_change || "/cart/change"}.js`,
          {
            method: "POST",
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify({
              id: line.key,
              quantity: 0,
              sections,
            }),
          },
        );
        if (!res.ok) throw new Error(res.statusText);
        const data = await res.json();
        if (Cart?.fillBasket) Cart.fillBasket(data.items);
        if (Cart?.updateTotals) Cart.updateTotals(data.item_count);
        ds_applyCartSections(data.sections);
      } catch {
        /* cart sync */
      } finally {
        DS_GIFT_STATE.removing = false;
      }
    };

    dsCartRewardsActiveSync = ds_syncCartGift;
    ds_syncCartGift();

    if (window._dsCartRewardsCartListener) {
      window.removeEventListener("theme:cart:updated", window._dsCartRewardsCartListener);
    }
    window._dsCartRewardsCartListener = () => {
      ds_dedupeCartUpsells();
      ds_syncCartGift();
    };
    window.addEventListener("theme:cart:updated", window._dsCartRewardsCartListener);

    Alpine.data("DSVideoCarousel", () => ({
      swiper: null,

      init() {
        this.$nextTick(() => this.mount());
      },

      destroy() {
        this.teardown();
      },

      teardown() {
        const root = this.$el;
        if (root._dsVideoControlsAbort) root._dsVideoControlsAbort.abort();
        const swEl = root.querySelector("[data-ds-video-swiper]");
        if (swEl?.swiper) swEl.swiper.destroy(true, true);
        this.swiper = null;
      },

      /**
       * Autoplay often fails if play() runs before the element can decode; retry after canplay.
       * @param {HTMLVideoElement} video
       */
      playActiveSlideVideo(video) {
        video.muted = window.dsShopableVideoMuted;
        const tryPlay = () => {
          video.play().catch(() => {});
        };
        if (video.readyState >= HTMLMediaElement.HAVE_CURRENT_DATA) {
          tryPlay();
        } else {
          video.addEventListener("canplay", tryPlay, { once: true });
          tryPlay();
        }
      },

      syncPlayback(activeIndex) {
        const root = this.$el;
        root.querySelectorAll("[data-slide-index]").forEach((slideEl) => {
          const i = parseInt(slideEl.dataset.slideIndex, 10);
          const isActive = i === activeIndex;

          const video = slideEl.querySelector("video.ds-shopable-video__video");
          if (!video) return;

          if (isActive) {
            this.playActiveSlideVideo(video);
          } else {
            video.pause();
            video.currentTime = 0;
          }
        });
      },

      updateUi(activeIndex) {
        const root = this.$el;
        root.querySelectorAll("[data-dot-index]").forEach((btn) => {
          const idx = parseInt(btn.getAttribute("data-dot-index") || "", 10);
          const on = idx === activeIndex;
          btn.classList.toggle("is-active", on);
          btn.setAttribute("aria-selected", on ? "true" : "false");
        });

        root.querySelectorAll("[data-ds-video-custom-ui]").forEach((el) => {
          const slide = el.closest("[data-slide-index]");
          const idx = slide ? parseInt(slide.dataset.slideIndex, 10) : -1;
          el.setAttribute("aria-hidden", idx === activeIndex ? "false" : "true");
        });
      },

      bindHtml5Controls() {
        const root = this.$el;
        if (root._dsVideoControlsAbort) root._dsVideoControlsAbort.abort();
        root._dsVideoControlsAbort = new AbortController();
        const { signal } = root._dsVideoControlsAbort;

        root.querySelectorAll(".ds-shopable-video__html5-wrap").forEach((wrap) => {
          const video = wrap.querySelector("video.ds-shopable-video__video");
          const progressEl = wrap.querySelector("[data-ds-video-progress]");
          const progressBar = wrap.querySelector("[data-ds-video-progress-bar]");
          const playBtn = wrap.querySelector("[data-ds-video-play-toggle]");
          const muteBtn = wrap.querySelector("[data-ds-video-mute-toggle]");
          const iconPlay = wrap.querySelector(".ds-shopable-video__ctrl-icon--play");
          const iconPause = wrap.querySelector(".ds-shopable-video__ctrl-icon--pause");
          const iconVolOn = wrap.querySelector(
            ".ds-shopable-video__ctrl-icon--volume-on",
          );
          const iconVolMuted = wrap.querySelector(
            ".ds-shopable-video__ctrl-icon--volume-muted",
          );
          if (!video) return;

          video.muted = window.dsShopableVideoMuted;

          let progressRafId = null;

          const updateProgress = () => {
            if (!video.duration || !Number.isFinite(video.duration)) return;
            const t = video.currentTime / video.duration;
            const pct = Math.min(100, Math.max(0, t * 100));
            if (progressBar) {
              progressBar.style.transform = `scaleX(${Math.min(1, Math.max(0, t))})`;
            }
            if (progressEl) {
              progressEl.setAttribute("aria-valuenow", String(Math.round(pct)));
            }
          };

          const progressTick = () => {
            if (signal.aborted) {
              progressRafId = null;
              return;
            }
            updateProgress();
            if (!video.paused && !video.ended) {
              progressRafId = requestAnimationFrame(progressTick);
            } else {
              progressRafId = null;
            }
          };

          const startProgressRaf = () => {
            if (signal.aborted) return;
            if (progressRafId != null) cancelAnimationFrame(progressRafId);
            progressRafId = requestAnimationFrame(progressTick);
          };

          const stopProgressRaf = () => {
            if (progressRafId != null) {
              cancelAnimationFrame(progressRafId);
              progressRafId = null;
            }
          };

          const syncPlayIcon = () => {
            const playing = !video.paused && !video.ended;
            if (iconPlay) iconPlay.hidden = playing;
            if (iconPause) iconPause.hidden = !playing;
            playBtn?.setAttribute("aria-label", playing ? "Pause" : "Play");
            playBtn?.setAttribute("aria-pressed", playing ? "true" : "false");
          };

          const syncMuteIcon = () => {
            const m = video.muted;
            if (iconVolOn) iconVolOn.hidden = m;
            if (iconVolMuted) iconVolMuted.hidden = !m;
            muteBtn?.setAttribute("aria-label", m ? "Unmute" : "Mute");
          };

          video.addEventListener("loadedmetadata", updateProgress, { signal });
          video.addEventListener("seeking", updateProgress, { signal });
          video.addEventListener("seeked", updateProgress, { signal });
          video.addEventListener(
            "ended",
            () => {
              stopProgressRaf();
              syncPlayIcon();
              updateProgress();
            },
            { signal },
          );
          video.addEventListener(
            "play",
            () => {
              syncPlayIcon();
              startProgressRaf();
            },
            { signal },
          );
          video.addEventListener(
            "pause",
            () => {
              stopProgressRaf();
              syncPlayIcon();
              updateProgress();
            },
            { signal },
          );
          video.addEventListener("volumechange", syncMuteIcon, { signal });

          playBtn?.addEventListener(
            "click",
            (e) => {
              e.stopPropagation();
              if (video.paused) video.play().catch(() => {});
              else video.pause();
            },
            { signal },
          );

          muteBtn?.addEventListener(
            "click",
            (e) => {
              e.stopPropagation();
              const nextMuted = !video.muted;
              window.dsShopableVideoMuted = nextMuted;
              root
                .querySelectorAll("video.ds-shopable-video__video")
                .forEach((v) => {
                  v.muted = nextMuted;
                });
            },
            { signal },
          );

          progressEl?.addEventListener(
            "click",
            (e) => {
              e.stopPropagation();
              if (!video.duration || !Number.isFinite(video.duration)) return;
              const rect = progressEl.getBoundingClientRect();
              const x = e.clientX - rect.left;
              const ratio = Math.min(1, Math.max(0, x / rect.width));
              video.currentTime = ratio * video.duration;
            },
            { signal },
          );

          progressEl?.addEventListener(
            "keydown",
            (e) => {
              if (e.key !== "ArrowLeft" && e.key !== "ArrowRight") return;
              e.stopPropagation();
              if (!video.duration || !Number.isFinite(video.duration)) return;
              const delta = e.key === "ArrowLeft" ? -5 : 5;
              video.currentTime = Math.min(
                video.duration,
                Math.max(0, video.currentTime + delta),
              );
            },
            { signal },
          );

          syncPlayIcon();
          syncMuteIcon();
          updateProgress();
          if (!video.paused) startProgressRaf();
        });
      },

      mount() {
        this.teardown();
        const root = this.$el;
        const swiperEl = root.querySelector("[data-ds-video-swiper]");
        if (!swiperEl || typeof window.Swiper === "undefined") return;

        const slides = root.querySelectorAll(".swiper-slide[data-slide-index]");
        if (slides.length < 1) return;

        const prevBtn = root.querySelector("[data-ds-video-prev]");
        const nextBtn = root.querySelector("[data-ds-video-next]");
        const self = this;

        const swiper = new window.Swiper(swiperEl, {
          centeredSlides: true,
          centeredSlidesBounds: true,
          slidesPerView: 1.5,
          spaceBetween: 12,
          watchSlidesProgress: true,
          slideToClickedSlide: true,
          loop: true,
          breakpoints: {
            990: {
              slidesPerView: 5,
              spaceBetween: 18,
            },
          },
          navigation: {
            prevEl: prevBtn,
            nextEl: nextBtn,
          },
          on: {
            init(sw) {
              const idx = sw.realIndex;
              self.syncPlayback(idx);
              self.updateUi(idx);
            },
            slideChange(sw) {
              const idx = sw.realIndex;
              self.syncPlayback(idx);
              self.updateUi(idx);
            },
          },
        });

        this.swiper = swiper;

        root.querySelectorAll("[data-dot-index]").forEach((btn) => {
          btn.onclick = () => {
            const idx = parseInt(btn.getAttribute("data-dot-index") || "", 10);
            if (Number.isFinite(idx)) swiper.slideTo(idx);
          };
        });

        this.bindHtml5Controls();

        queueMicrotask(() => {
          if (!root.isConnected) return;
          const s = root.querySelector("[data-ds-video-swiper]")?.swiper;
          if (s) {
            self.syncPlayback(s.realIndex);
            self.updateUi(s.realIndex);
          }
        });
      },
    }));
  });

  const scrollActiveThumbIntoStripViewport = (strip, mediaId) => {
    const viewport = strip?.querySelector?.(".product-media--thumbs-viewport");
    const activeThumb = strip?.querySelector?.(
      `.product-media--thumb[data-id="${mediaId}"]`,
    );
    if (!viewport || !activeThumb) return;
    const maxLeft = Math.max(0, viewport.scrollWidth - viewport.clientWidth);
    const targetLeft = Math.min(
      Math.max(
        0,
        activeThumb.offsetLeft -
          (viewport.clientWidth - activeThumb.offsetWidth) / 2,
      ),
      maxLeft,
    );
    viewport.scrollTo({ left: targetLeft, behavior: "smooth" });
  };

  const initProductThumbsStripNav = () => {
    document.querySelectorAll(".product-media--thumbs-shell").forEach((shell) => {
      if (shell.dataset.stripNavInit === "true") return;
      shell.dataset.stripNavInit = "true";
      const viewport = shell.querySelector(".product-media--thumbs-viewport");
      const prev = shell.querySelector(".product-media--thumbs-nav--prev");
      const next = shell.querySelector(".product-media--thumbs-nav--next");
      if (!viewport || !prev || !next) return;

      const pageDelta = () => viewport.clientWidth;

      const updateStripNav = () => {
        const max = viewport.scrollWidth - viewport.clientWidth;
        if (max <= 2) {
          prev.disabled = true;
          next.disabled = true;
          return;
        }
        prev.disabled = viewport.scrollLeft <= 2;
        next.disabled = viewport.scrollLeft >= max - 2;
      };

      prev.addEventListener("click", (e) => {
        e.preventDefault();
        viewport.scrollBy({
          left: -pageDelta(),
          behavior: "smooth",
        });
      });
      next.addEventListener("click", (e) => {
        e.preventDefault();
        viewport.scrollBy({
          left: pageDelta(),
          behavior: "smooth",
        });
      });
      viewport.addEventListener("scroll", updateStripNav, { passive: true });
      new ResizeObserver(updateStripNav).observe(viewport);
      updateStripNav();
    });
  };

  const initProductMediaDesktopNav = () => {
    document
      .querySelectorAll(
        'product-media-variants.product-media--variants--root[data-layout="thumbnails"] .product-media--main-shell',
      )
      .forEach((shell) => {
        if (shell.dataset.navInit === "true") return;
        shell.dataset.navInit = "true";
        const root = shell.closest("product-media-variants");
        if (!root) return;
        const productId = root.dataset.id;
        const prev = shell.querySelector(".product-media--nav--prev");
        const next = shell.querySelector(".product-media--nav--next");
        if (!prev || !next || !productId) return;

        const getMedia = () => [
          ...root.querySelectorAll(".product-media--container .product-media--root"),
        ];
        const getIndex = () => {
          const media = getMedia();
          return media.findIndex((el) => el.getAttribute("data-active") === "true");
        };

        const syncThumbs = (mediaId) => {
          const strip = document.querySelector(
            `product-thumbs-element[data-id="${productId}"]`,
          );
          if (!strip) return;
          strip.querySelectorAll(".product-media--thumb").forEach((t) => {
            t.setAttribute(
              "data-active",
              parseInt(t.dataset.id, 10) === mediaId,
            );
          });
          scrollActiveThumbIntoStripViewport(strip, mediaId);
        };

        const updateNavState = () => {
          const media = getMedia();
          const n = media.length;
          let idx = getIndex();
          if (idx === -1) idx = 0;
          if (n < 2) {
            prev.disabled = true;
            next.disabled = true;
            return;
          }
          prev.disabled = idx <= 0;
          next.disabled = idx >= n - 1;
        };

        const go = (delta) => {
          const media = getMedia();
          if (media.length < 2) return;
          let idx = getIndex();
          if (idx === -1) idx = 0;
          const nextIdx = idx + delta;
          if (nextIdx < 0 || nextIdx >= media.length) return;
          const id = parseInt(media[nextIdx].dataset.id, 10);
          window.trigger(`theme:product:${productId}:mediaChanged`, { id });
          syncThumbs(id);
          updateNavState();
        };

        prev.addEventListener("click", (e) => {
          e.preventDefault();
          go(-1);
        });
        next.addEventListener("click", (e) => {
          e.preventDefault();
          go(1);
        });

        const onMediaOrVariant = () => {
          updateNavState();
          requestAnimationFrame(() => {
            const media = getMedia();
            let idx = getIndex();
            if (idx === -1) idx = 0;
            const m = media[idx];
            if (!m) return;
            const strip = document.querySelector(
              `product-thumbs-element[data-id="${productId}"]`,
            );
            scrollActiveThumbIntoStripViewport(
              strip,
              parseInt(m.dataset.id, 10),
            );
          });
        };
        window.addEventListener(
          `theme:product:${productId}:mediaChanged`,
          onMediaOrVariant,
        );
        window.addEventListener(
          `theme:product:${productId}:variantChanged`,
          onMediaOrVariant,
        );
        updateNavState();
      });
  };

  document.addEventListener("DOMContentLoaded", () => {
    initProductThumbsStripNav();
    initProductMediaDesktopNav();
  });
  document.addEventListener("shopify:section:load", () => {
    initProductThumbsStripNav();
    initProductMediaDesktopNav();
  });
})();
